import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, OnChanges, EventEmitter, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TableStateService } from '../../../../shared/services/table-state.service';
import { MonitoredTrain } from "../../models/monitored-train";
import { TrainTreeStructurePipe } from '../../pipes/train-tree-structure.pipe';
import { ExportFilenameService } from '../../../../shared/services/export-filename.service';
import { DataImportExportService } from '../../../../shared/services/data-import-export.service';

@Component({
  selector: 'app-monitored-train-devices',
  template: require('./monitored-train-devices.component.html'),
  styles: [require('./monitored-train-devices.component.sass')],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MonitoredTrainDevicesComponent implements OnInit, OnChanges, OnDestroy {

  @Input() projectId: number;
  @Input() isSingleTrain: boolean;
  @Input() trains: MonitoredTrain[];
  @Input() trainTableKey: string;

  @Output() investigateTrain = new EventEmitter<MonitoredTrain>();

  devices: any[];
  languageChangeSubscription: Subscription;

  tableConfiguration: any = {
    paginate: true,
    sortableMode: 'multiple',
    defaultPagination: 100,
    itemsPerPageOptions: [50,100,250,500],
    globalFilterFields: ['train.projectName', 'train.displayedName' , 'coach.label', 'hostname', 'ipAddr', 'macAddress', 'serial', 'config', 'firmware', 'type', 'paintedCoachId', 'position'],
    exportFilename: ExportFilenameService.generate('Fleet-Overview-Configuration-Status')
 }

 columns: any[] ;

  constructor(
    private trainTreeStructurePipe: TrainTreeStructurePipe,
    private translate: TranslateService,
    public tableState: TableStateService,
    public dataImportExportService: DataImportExportService
  ) { 
    this.languageChangeSubscription = this.translate.onLangChange.subscribe ( () => {
      this._refreshConfigStatusTable();
      this._processDevicesData();
    } );
  }

  ngOnInit(): void {
    this._refreshConfigStatusTable();
  }

  ngOnChanges(): void {
    if (this.isSingleTrain) {
      const currentTrain = this.trains[0];
      if (currentTrain) {
        this.tableConfiguration.exportFilename = ExportFilenameService.generate('Train-Information-Configuration-Status', undefined, undefined, currentTrain.name);
      }     
    }
    this._processDevicesData();
  }

  _processDevicesData = () => {
    this.devices = [];
    for (let index = 0; index < this.trains.length; index++) {
      const train = this.trains[index];
      const trainStructure = this.trainTreeStructurePipe.transform(train, train && train.trainConfiguration);
      if (trainStructure.length > 0) {
        this.devices = [
          ...this.devices,
          ...trainStructure[0].children ? trainStructure[0].children.reduce((acc: any, coach) => {
            return coach.children ? [
              ...acc,
              ...coach.children.map(device => {
                return {
                  train: {
                    ...train,
                    displayedName:`${train.name} (${train.trainId})`
                  },
                  coach: {
                    label: coach.label,
                    style: coach.data.style
                  },
                  ...device.data?.device
                }
              })
            ]: acc;
          }, []) : []
      ]
      }
    }
    
  }

  _refreshConfigStatusTable = () => {
    this.columns = [{
      header: this.translate.instant('I18N_MONITORING.PROJECT'),
      field: 'train.projectName',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.TRAIN'),
      field: 'train.displayedName',
      type: 'text'
    },{
      header: this.translate.instant('I18N_MONITORING.COACH'),
      field: 'coach.label',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.DEVICE_ZABBIX_HOST'),
      field: 'hostname',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.DEVICE_IP_ADDRESS'),
      field: 'ipAddr',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.DEVICE_MAC_ADDRESS'),
      field: 'macAddress',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.DEVICE_SERIAL'),
      field: 'serial',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.DEVICE_CONFIG'),
      field: 'config',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.DEVICE_FIRMWARE'),
      field: 'firmware',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.DEVICE_TYPE'),
      field: 'type',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.PAINTED_CAR_NUMBER'),
      field: 'paintedCoachId',
      type: 'text'
    },
    {
      header: this.translate.instant('I18N_MONITORING.DEVICE_POSITION'),
      field: 'position',
      type: 'text'
    }, {
      header: this.translate.instant('I18N_GENERAL.ACTIONS'),
      type: null,
      disableSorting: true,
      disableSearch: true,
      excludeExport: true
    }].map ( (column: any) => {
      column.visible = true;
      if (column.field === 'train.projectName') {
          column.visible = !(this.projectId > 0);
      }
      return column;
  } ).filter(column => column.visible);
  }

  openDeviceConfiguration = (ev: Event, device: any, linkType: string) => {
    ev.stopPropagation();
    ev.preventDefault();
    window.open(`${linkType}://${device.surfIPAddress}`, "_blank");
  }

  onInvestigateTrain = (train: MonitoredTrain) => {
    this.investigateTrain.emit(train)
  }

  ngOnDestroy(): void {
   this.languageChangeSubscription.unsubscribe();
  }
}
